import React, { FunctionComponent, useRef } from 'react';
import { path, isEmpty, divide, compose, __ } from 'ramda';
import withStyles from 'react-jss';
import { Grid, Header, Icon } from 'semantic-ui-react';
import { format } from 'd3-format';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  Tooltip,
  Bar,
  XAxis,
  YAxis,
} from 'recharts';

import { chartMargin } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantLift/MerchantLift.helpers';
import {
  QueryResponse,
  SubscriberPurchasePattern,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOverview/sections/KeyMetricGraphs/KeyMetricGraphs.helpers';

import { baseStyles } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantLift/MerchantLift.styles';
import DownloadWidget from 'components/DownloadWidget';
import { ClassProp } from 'helpers/types';
import { theme } from 'config';
import CustomTooltip from 'components/CustomTooltip';

type Props = {
  classes: ClassProp<typeof styles>;
  response: QueryResponse;
};

const styles = baseStyles();

const tooltipFields = (formatter: Function) => [
  {
    formatter,
    label: 'Percent of Total Visitor',
    value: 'percentOfMerchantTotal',
  },
];

const formatter = compose(format('.2%'), divide(__, 100));

const renderHeader = (title: string, classes: ClassProp<typeof styles>) => (
  <Header className={classes.header}>
    <Icon className={classes.headerIcon} name="chart bar" />
    {title}
  </Header>
);

const purchasesBySubWithMulPurchasesBySegment = path<
  SubscriberPurchasePattern[]
>(['data', 'purchasesBySubWithMulPurchasesBySegment']);

const Frequency: FunctionComponent<Props> = ({ classes, response }) => {
  const contentRef = useRef(null);
  const purchasesBySub = purchasesBySubWithMulPurchasesBySegment(response);

  return (
    <>
      <div ref={contentRef} className={classes.ref}>
        <Grid.Row className={classes.chartContent}>
          <Grid.Column
            width={16}
            columns={1}
            textAlign="center"
            verticalAlign="middle"
          >
            <Grid.Column>
              {isEmpty(purchasesBySub) ? (
                <Header as="h3">
                  There is no data to display at this time.
                </Header>
              ) : (
                <div className={classes.chart}>
                  <ResponsiveContainer>
                    <BarChart data={purchasesBySub} margin={chartMargin}>
                      <CartesianGrid vertical={false} />
                      <XAxis dataKey="totalPurchases" />
                      <YAxis />

                      <Tooltip
                        cursor={{
                          fill: '#0000000f',
                        }}
                        content={
                          <CustomTooltip
                            fields={tooltipFields(formatter)}
                            xAxis={{
                              label: 'Number of Visits',
                              key: 'totalPurchases',
                            }}
                          />
                        }
                      />

                      <Bar
                        dataKey="percentOfMerchantTotal"
                        fill={theme.violet}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </Grid.Column>
          </Grid.Column>

          <Grid.Column width={16} className={classes.legend}>
            {renderHeader('Percent of Total Visitor', classes)}
          </Grid.Column>
        </Grid.Row>
      </div>

      <DownloadWidget
        nodeRef={contentRef}
        className={classes.download}
        options={['PNG', 'PDF']}
        imageName={'Revenue'}
      />
    </>
  );
};

export default withStyles(styles)(Frequency);
